import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  ArrowLeft, Store, MapPin, Phone, Mail, Users, Utensils,
  AlertTriangle, CheckCircle, Ban, CreditCard, Calendar, Trash2
} from "lucide-react";
import { cn, formatCurrency, formatDate } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RestaurantDetail = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusDialog, setStatusDialog] = useState(false);
  const [subscriptionDialog, setSubscriptionDialog] = useState(false);
  
  const [statusForm, setStatusForm] = useState({
    status: "active",
    reason: "",
    internal_note: "",
  });
  
  const [subscriptionForm, setSubscriptionForm] = useState({
    plan: "starter",
    subscription_status: "trial",
    trial_ends_at: "",
  });

  useEffect(() => {
    fetchData();
  }, [restaurantId]);

  const fetchData = async () => {
    try {
      const [resResponse, usersResponse, catsResponse, itemsResponse] = await Promise.all([
        axios.get(`${API}/admin/restaurants/${restaurantId}`),
        axios.get(`${API}/admin/users/${restaurantId}`),
        axios.get(`${API}/admin/restaurants/${restaurantId}/categories`),
        axios.get(`${API}/admin/restaurants/${restaurantId}/items`),
      ]);
      setRestaurant(resResponse.data);
      setUsers(usersResponse.data);
      setCategories(catsResponse.data);
      setItems(itemsResponse.data);
      
      setStatusForm({
        status: resResponse.data.status || "active",
        reason: resResponse.data.status_reason || "",
        internal_note: resResponse.data.internal_note || "",
      });
      
      setSubscriptionForm({
        plan: resResponse.data.subscription_plan || "starter",
        subscription_status: resResponse.data.subscription_status || "trial",
        trial_ends_at: resResponse.data.trial_ends_at || "",
      });
    } catch (error) {
      console.error("Failed to fetch restaurant:", error);
      toast.error("Failed to load restaurant");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async () => {
    try {
      await axios.put(`${API}/admin/restaurants/${restaurantId}/status`, statusForm);
      toast.success("Status updated");
      fetchData();
      setStatusDialog(false);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updateSubscription = async () => {
    try {
      await axios.put(`${API}/admin/restaurants/${restaurantId}/subscription`, subscriptionForm);
      toast.success("Subscription updated");
      fetchData();
      setSubscriptionDialog(false);
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm("Delete this category and all its items?")) return;
    try {
      await axios.delete(`${API}/admin/restaurants/${restaurantId}/categories/${categoryId}`);
      toast.success("Category deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/admin/restaurants/${restaurantId}/items/${itemId}`);
      toast.success("Item deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-700",
      inactive: "bg-gray-100 text-gray-700",
      suspended: "bg-red-100 text-red-700",
    };
    return styles[status] || styles.active;
  };

  const getSubscriptionBadge = (status) => {
    const styles = {
      trial: "bg-blue-100 text-blue-700",
      active: "bg-green-100 text-green-700",
      expired: "bg-amber-100 text-amber-700",
      suspended: "bg-red-100 text-red-700",
    };
    return styles[status] || styles.trial;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!restaurant) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Restaurant not found</p>
          <Button onClick={() => navigate("/admin/restaurants")} className="mt-4">
            Back to Restaurants
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="restaurant-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/admin/restaurants")}>
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Store className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold">{restaurant.name}</h1>
                <p className="text-sm text-muted-foreground">{restaurant.slug}.dalabsom.com</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={cn(getStatusBadge(restaurant.status))}>
              {restaurant.status}
            </Badge>
            <Badge className={cn(getSubscriptionBadge(restaurant.subscription_status))}>
              {restaurant.subscription_status}
            </Badge>
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="menu">Menu ({items.length})</TabsTrigger>
            <TabsTrigger value="users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="subscription">Subscription</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-6">
            {/* Restaurant Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{restaurant.address}, {restaurant.city}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{restaurant.contact_phone}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{restaurant.contact_email}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Status Control</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Current Status</span>
                    <Badge className={cn(getStatusBadge(restaurant.status), "capitalize")}>
                      {restaurant.status}
                    </Badge>
                  </div>
                  {restaurant.status_reason && (
                    <div>
                      <span className="text-sm text-muted-foreground">Reason:</span>
                      <p className="text-sm">{restaurant.status_reason}</p>
                    </div>
                  )}
                  <Button onClick={() => setStatusDialog(true)} variant="outline" className="w-full">
                    Change Status
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{categories.length}</p>
                  <p className="text-sm text-muted-foreground">Categories</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{items.length}</p>
                  <p className="text-sm text-muted-foreground">Menu Items</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{users.length}</p>
                  <p className="text-sm text-muted-foreground">Users</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold capitalize">{restaurant.category}</p>
                  <p className="text-sm text-muted-foreground">Type</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="menu" className="mt-6 space-y-4">
            {categories.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Utensils className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No menu items yet</p>
                </CardContent>
              </Card>
            ) : (
              categories.map((category) => (
                <Card key={category.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{category.name}</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => deleteCategory(category.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {items.filter(i => i.category_id === category.id).map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <div>
                            <span className="font-medium">{item.name}</span>
                            <span className="text-muted-foreground ml-2">{formatCurrency(item.price)}</span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => deleteItem(item.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Name</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Email</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Role</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b last:border-0">
                        <td className="p-4">{user.name}</td>
                        <td className="p-4 text-sm">{user.email}</td>
                        <td className="p-4">
                          <Badge variant="outline" className="capitalize">{user.role}</Badge>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {formatDate(user.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscription" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Subscription Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Current Plan</span>
                    <p className="font-semibold capitalize">{restaurant.subscription_plan}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Status</span>
                    <p>
                      <Badge className={cn(getSubscriptionBadge(restaurant.subscription_status), "capitalize")}>
                        {restaurant.subscription_status}
                      </Badge>
                    </p>
                  </div>
                  {restaurant.trial_ends_at && (
                    <div>
                      <span className="text-sm text-muted-foreground">Trial Ends</span>
                      <p className="font-medium">{formatDate(restaurant.trial_ends_at)}</p>
                    </div>
                  )}
                </div>
                <Button onClick={() => setSubscriptionDialog(true)} className="w-full">
                  Manage Subscription
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Status Dialog */}
        <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Restaurant Status</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Active
                      </div>
                    </SelectItem>
                    <SelectItem value="inactive">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        Inactive
                      </div>
                    </SelectItem>
                    <SelectItem value="suspended">
                      <div className="flex items-center gap-2">
                        <Ban className="w-4 h-4 text-red-500" />
                        Suspended
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {statusForm.status !== "active" && (
                <>
                  <div className="space-y-2">
                    <Label>Reason (visible to restaurant)</Label>
                    <Select value={statusForm.reason} onValueChange={(v) => setStatusForm({ ...statusForm, reason: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="subscription_unpaid">Subscription unpaid</SelectItem>
                        <SelectItem value="policy_violation">Policy violation</SelectItem>
                        <SelectItem value="manual_suspension">Manual suspension</SelectItem>
                        <SelectItem value="account_review">Account under review</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Internal Note (admin only)</Label>
                    <Textarea
                      value={statusForm.internal_note}
                      onChange={(e) => setStatusForm({ ...statusForm, internal_note: e.target.value })}
                      placeholder="Add internal notes..."
                    />
                  </div>
                </>
              )}
              
              <Button onClick={updateStatus} className="w-full">
                Update Status
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Subscription Dialog */}
        <Dialog open={subscriptionDialog} onOpenChange={setSubscriptionDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Manage Subscription</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={subscriptionForm.plan} onValueChange={(v) => setSubscriptionForm({ ...subscriptionForm, plan: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={subscriptionForm.subscription_status} onValueChange={(v) => setSubscriptionForm({ ...subscriptionForm, subscription_status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Trial Ends</Label>
                <Input
                  type="date"
                  value={subscriptionForm.trial_ends_at ? subscriptionForm.trial_ends_at.split("T")[0] : ""}
                  onChange={(e) => setSubscriptionForm({ ...subscriptionForm, trial_ends_at: e.target.value })}
                />
              </div>
              <Button onClick={updateSubscription} className="w-full">
                Update Subscription
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default RestaurantDetail;
